<template>
  <div v-if="buttons.length" class="section-detailed-review pb-40">
    <h2 class="heading-4-bd mb-12">Reviews from Past Travellers</h2>
    <div class="flex mb-16">
      <!-- Both categories → clickable tag toggles (same tag look for consistency) -->
      <template v-if="buttons.length > 1">
        <button
          v-for="(button, index) in buttons"
          :key="index"
          type="button"
          class="review-tag mr-8"
          :class="{ 'is-active': sectionActive === button.id }"
          :aria-pressed="sectionActive === button.id"
          @click.prevent="handleChangeSection(button.id)"
        >
          {{ button.label }}
        </button>
      </template>
      <!-- Only one category → static Badge/Tag (not a toggle) -->
      <span v-else class="review-tag">{{ buttons[0].label }}</span>
    </div>
    <SectionReviewDestination
      ref="sectionReviewDestinationRef"
      v-show="sectionActive === SectionType.Destination"
      :designer="designer"
      :country-ids="countryIds"
    />
    <SectionReviewDesigner ref="sectionReviewDesignerRef" v-show="sectionActive === SectionType.Designer" :designer="designer" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { isEmpty } from '~/helper'
import SectionReviewDesigner from './SectionReviewDesigner.vue'
import SectionReviewDestination from './SectionReviewDestination.vue'
import { useTripPlanStore } from '~/store/pinia/tripPlan'

enum SectionType {
  Destination,
  Designer,
}

const tripPlanStore = useTripPlanStore()

const dataTrip = computed(() => tripPlanStore.dataTrip)
const designer = computed(() => tripPlanStore.designer)
const countryIds = computed(() => {
  if (isEmpty(dataTrip.value)) return []
  return dataTrip.value?.country_ids || []
})

const sectionActive = ref<SectionType>(SectionType.Destination)
const buttons = ref([
  { id: SectionType.Destination, label: 'Destination Reviews' },
  { id: SectionType.Designer, label: 'Local Designer Reviews' },
])
const sectionReviewDestinationRef = ref<InstanceType<typeof SectionReviewDestination> | null>(null)
const sectionReviewDesignerRef = ref<InstanceType<typeof SectionReviewDesigner> | null>(null)
const reviewsLocalDesigner = computed(() => sectionReviewDesignerRef.value?.reviews || [])
function handleChangeSection(section: SectionType) {
  sectionActive.value = section
}
watch(
  () => sectionReviewDestinationRef.value?.reviews,
  (value) => {
    if (value) {
      if (!value.total) {
        buttons.value = buttons.value.filter((button) => button.id !== SectionType.Destination)
        sectionActive.value = SectionType.Designer
      }
    }
  },
  { deep: true },
)
watch(
  () => sectionReviewDesignerRef.value?.reviews,
  (value) => {
    if (value) {
      if (!value.total) {
        buttons.value = buttons.value.filter((button) => button.id !== SectionType.Designer)
        sectionActive.value = SectionType.Destination
      }
    }
  },
  { deep: true },
)

defineExpose({
  reviewsLocalDesigner,
})
</script>

<style lang="scss" scoped>
// v2 Badge/Tag (guideline) — green pill for the review-category label(s).
// Single category → static <span> (solid). Two categories → clickable toggles that keep the
// same tag look: selected = solid green, unselected = green outline.
.review-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid $brand-green;
  border-radius: $radius-pill;
  background: $brand-green;
  color: $white;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}
// interactive toggles (two categories) — clickable, keyboard-focusable
button.review-tag {
  cursor: pointer;
  transition: background-color $dur-base $ease-out, color $dur-base $ease-out;
  @include focus-ring;

  &:not(.is-active) {
    background: transparent;
    color: $link;

    &:hover {
      background: rgba($brand-green, 0.1);
    }
  }
}
</style>
